<script lang="ts">
  import { getModalStore, getToastStore } from '@skeletonlabs/skeleton'

  import type { LaunchEvalPayload } from './eval-run'
  import LaunchEvalForm from './LaunchEvalForm.svelte'
  import { openRunOptionsModal, type RunOptionsMeta } from '../runs/llm-runs'
  import CloseModalButton from '@components/CloseModalButton.svelte'

  interface ModalParentProps {
    onClose: () => void
    regionFooter: string
    buttonTextCancel: string
    buttonTextConfirm?: string
  }

  interface Props {
    parent: ModalParentProps
  }

  type ModalValue = Partial<Pick<LaunchEvalPayload, 'modelId' | 'sourceRunId'>> & {
    title?: string
  }

  let { parent }: Props = $props()

  interface LaunchEvalFormHandle {
    submit: () => void
  }

  const modalStore = getModalStore()
  const toastStore = getToastStore()
  let launchEvalForm: LaunchEvalFormHandle | null = $state(null)
  let isLaunching = $state(false)

  const modalValue = $derived(($modalStore[0]?.value ?? {}) as ModalValue)
  const title = $derived(
    modalValue.title ??
      (modalValue.sourceRunId ? 'Evaluate Unlearned Model' : 'Launch Model Evaluation'),
  )

  const initialUseSource = $derived(modalValue.sourceRunId ? 'run' : 'model')
  const initialPayload = $derived({
    ...(modalValue.modelId ? { modelId: modalValue.modelId } : {}),
    ...(modalValue.sourceRunId ? { sourceRunId: modalValue.sourceRunId } : {}),
  })
  const allowSourceSelection = $derived(!modalValue.modelId && !modalValue.sourceRunId)

  const runOptions = $derived(($modalStore[0]?.meta?.runOptions ?? null) as RunOptionsMeta | null)
  const canReturnToRunOptions = $derived(Boolean(runOptions))

  const closeModal = () => modalStore.close()
  const onLaunch = () => {
    launchEvalForm?.submit()
  }

  const reopenRunOptions = async () => {
    const runOptionsSnapshot = runOptions
    if (!runOptionsSnapshot) {
      return
    }
    modalStore.close()
    await openRunOptionsModal({
      modalStore,
      toastStore,
      runOptions: runOptionsSnapshot,
    })
  }
</script>

{#if $modalStore[0]}
  <div
    class="card max-h-[90vh] w-[min(1000px,calc(100vw-2rem))] max-w-none overflow-y-auto shadow-xl"
    role="dialog"
  >
    <header
      class="sticky top-0 z-10 flex min-h-[4.5rem] items-center justify-between border-b border-surface-300 bg-surface-50 px-8 text-2xl font-medium dark:border-surface-600 dark:bg-surface-800"
    >
      <span>{title}</span>
      <CloseModalButton onClose={parent.onClose} />
    </header>
    <article class="px-8 py-5">
      <LaunchEvalForm
        bind:this={launchEvalForm}
        bind:isLaunching
        {initialUseSource}
        {initialPayload}
        {allowSourceSelection}
        onSuccess={closeModal}
      />
    </article>
    <footer
      class="modal-footer sticky bottom-0 border-t border-surface-300 bg-surface-50 px-8 py-5 dark:border-surface-600 dark:bg-surface-800 {parent.regionFooter}"
    >
      <div class="flex w-full items-center gap-2">
        {#if canReturnToRunOptions}
          <button class="variant-filled-surface btn" onclick={reopenRunOptions}>
            Back to run options
          </button>
        {/if}
        <div class="ml-auto flex items-center gap-2 max-sm:w-full max-sm:[&>button]:flex-1">
          <button class="variant-ghost-surface btn" onclick={closeModal}>
            {parent.buttonTextCancel}
          </button>
          <button
            class="variant-filled-primary btn"
            type="button"
            onclick={onLaunch}
            disabled={isLaunching}
          >
            {parent.buttonTextConfirm ?? 'Launch'}
          </button>
        </div>
      </div>
    </footer>
  </div>
{/if}
